/**
 * F-191 phase 1: light, dark or automatic.
 *
 * The community picks a mode in Marca e aparência (`appearance.mode`); the API
 * answers with `effectiveMode`, which is `light` while the SuperAdmin's switch
 * (`darkModeAllowed`) is off. A member can override it in Minha Conta ›
 * Aparência, for that browser and that community only (`localStorage`, keyed
 * by host). The result is the `dark` class on `<html>`, which swaps the color
 * tokens in index.css.
 *
 * The same rule runs before the app loads, in the inline script of
 * `index.html` (it reads the mode the server wrote and the member's choice),
 * so the page does not flash white. Keep both in step.
 */

export type AppearanceMode = 'light' | 'dark' | 'auto'

/** What the member picked: the community's mode, a fixed one, or the device's. */
export type MemberAppearance = 'community' | 'light' | 'dark' | 'device'

export const MEMBER_APPEARANCES: MemberAppearance[] = ['community', 'light', 'dark', 'device']
export const COMMUNITY_MODES: AppearanceMode[] = ['light', 'dark', 'auto']

/** The appearance block every community payload carries. */
export interface CommunityAppearance {
  mode: AppearanceMode
  effectiveMode: AppearanceMode
  darkLogoUrl: string | null
  darkModeAllowed: boolean
  /** F-191 phases 2 and 5: the side menu's starting state, side menu or top bar, hidden native elements (see `navigation.ts`). */
  navigation?: { sidebarDefault?: 'full' | 'rail'; layout?: 'sidebar' | 'top'; hiddenNative?: string[] }
  /** F-191 phase 4: the active theme's id (null while the community has none saved) and its values. */
  activeThemeId?: string | null
  theme?: unknown
}

const MEMBER_KEY = 'memberfy:appearance'
const MIRROR_KEY = 'memberfy:appearance:community'

const hostKey = (base: string) => (typeof window === 'undefined' ? base : `${base}:${window.location.host}`)

const read = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

const write = (key: string, value: string | null) => {
  try {
    if (value === null) window.localStorage.removeItem(key)
    else window.localStorage.setItem(key, value)
  } catch {
    /* Private window or blocked storage: the choice lasts until the tab closes. */
  }
}

const isMode = (value: unknown): value is AppearanceMode => value === 'light' || value === 'dark' || value === 'auto'

/** The appearance as the API sent it, or the light default for an older API. */
export function appearanceOf(value: unknown): CommunityAppearance {
  const raw = (value && typeof value === 'object' ? value : {}) as Partial<CommunityAppearance>
  const darkModeAllowed = raw.darkModeAllowed === true
  const mode = isMode(raw.mode) ? raw.mode : 'light'
  return {
    mode,
    effectiveMode: darkModeAllowed && isMode(raw.effectiveMode) ? raw.effectiveMode : 'light',
    darkLogoUrl: typeof raw.darkLogoUrl === 'string' && raw.darkLogoUrl ? raw.darkLogoUrl : null,
    darkModeAllowed,
  }
}

export function readMemberAppearance(): MemberAppearance {
  const value = read(hostKey(MEMBER_KEY))
  return (MEMBER_APPEARANCES as string[]).includes(value ?? '') ? (value as MemberAppearance) : 'community'
}

export function writeMemberAppearance(value: MemberAppearance): void {
  write(hostKey(MEMBER_KEY), value === 'community' ? null : value)
}

const systemPrefersDark = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches

/**
 * Whether the screen is dark. Without the SuperAdmin's switch nothing is: the
 * switch exists because the dark theme is still being checked community by
 * community, and the member's choice does not get around it.
 */
export function isDark(appearance: CommunityAppearance, member: MemberAppearance): boolean {
  if (!appearance.darkModeAllowed) return false
  const mode = member === 'community' ? appearance.effectiveMode : member === 'device' ? 'auto' : member
  return mode === 'dark' || (mode === 'auto' && systemPrefersDark())
}

/** Puts (or takes) the `dark` class on `<html>`, and mirrors the community's mode for the boot script. */
export function applyDark(dark: boolean, appearance?: CommunityAppearance): void {
  if (typeof document === 'undefined') return
  document.documentElement.classList.toggle('dark', dark)
  if (appearance) write(hostKey(MIRROR_KEY), appearance.darkModeAllowed ? appearance.effectiveMode : null)
}

/** Calls `onChange` when the device switches between light and dark. */
export function watchSystemTheme(onChange: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => undefined
  const query = window.matchMedia('(prefers-color-scheme: dark)')
  query.addEventListener('change', onChange)
  return () => query.removeEventListener('change', onChange)
}

/** Event fired when the member changes their choice, so the provider re-applies it in this tab. */
export const MEMBER_APPEARANCE_EVENT = 'memberfy:appearance-change'
